export function formatPhoneDisplay(phone?: string | null): string {
  if (!phone) return "-";
  const digits = String(phone).replace(/\D/g, "").replace(/^65/, "").slice(0, 8);
  if (digits.length !== 8) return "-";
  return `+65 ${digits.slice(0, 4)} ${digits.slice(4)}`;
}

export function formatDate(dateStr?: string | Date | null): string {
  if (!dateStr) return "-";
  let dateObj: Date;
  if (typeof dateStr === "string") {
    const normalized = dateStr.replace(/\//g, "-");
    dateObj = new Date(
      normalized.length === 10 ? `${normalized}T00:00:00+08:00` : normalized,
    );
  } else {
    dateObj = dateStr;
  }
  if (isNaN(dateObj.getTime())) return "Invalid Date";
  return new Intl.DateTimeFormat("en-SG", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Singapore",
  }).format(dateObj);
}

export function formatTime12Hour(time?: string | null): string {
  if (!time || !time.includes(":")) return "-";

  const [hRaw, mRaw] = time.split(":");
  const h = Number(hRaw);
  const m = Number(mRaw);

  if (Number.isNaN(h) || Number.isNaN(m)) return "-";

  const period = h < 12 ? "AM" : "PM";
  const hour = h % 12 || 12;
  return `${String(hour).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
}

export function formatTime24Hour(time?: string | null): string {
  if (!time || !time.includes(":")) return "-";

  const [hRaw] = time.split(".");
  const [hours, minutes] = hRaw.split(":");
  const h = Number(hours);
  const m = Number(minutes);

  if (!Number.isFinite(h) || !Number.isFinite(m)) return "-";

  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function formatTimeLabel(time: string): string {
  if (!time.includes(":")) return time;

  const [hours, minutes] = time.split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return time;

  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${String(displayHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${period}`;
}

/** Compact 12-hour time, no leading zero on hour: "9:00 AM", "1:30 PM" */
export function formatTime12Compact(time?: string | null): string {
  if (!time || !time.includes(":")) return "-";
  const [hRaw, mRaw] = time.split(":");
  const h = Number(hRaw);
  const m = Number(mRaw);
  if (Number.isNaN(h) || Number.isNaN(m)) return "-";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
}

export function formatTimeSlot12h(start?: string | null, end?: string | null, estimated = false, showDuration = true): string {
  if (!start || !end) return "-";

  const fmt = (t: string): string => {
    const [hRaw, mRaw] = t.split(":");
    const h = Number(hRaw);
    const m = Number(mRaw);
    if (Number.isNaN(h) || Number.isNaN(m)) return "-";
    const period = h < 12 ? "AM" : "PM";
    const hour = h % 12 || 12;
    return `${String(hour).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
  };

  const startStr = fmt(start);
  const endStr = fmt(end);
  if (startStr === "-" || endStr === "-") return "-";

  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  const totalMin = eh * 60 + em - (sh * 60 + sm);
  if (showDuration && totalMin > 0) {
    const hours = totalMin / 60;
    const label = Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
    const unit = hours === 1 ? "hr" : "hrs";
    return `${startStr} - ${endStr} (${estimated ? "est. " : ""}${label} ${unit})`;
  }
  return `${startStr} - ${endStr}`;
}
